import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export const InstallPWA = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed this session
    if (sessionStorage.getItem('pwa-dismissed')) {
      setDismissed(true);
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Check for iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = window.navigator.standalone;
    
    if (isIOS && !isInStandaloneMode) {
      // Delay showing iOS prompt
      setTimeout(() => setShowIOSPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowIOSPrompt(false);
    setInstallPrompt(null);
    sessionStorage.setItem('pwa-dismissed', 'true');
  };

  // Don't show if dismissed or nothing to show
  if (dismissed || (!installPrompt && !showIOSPrompt)) {
    return null;
  }

  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-slide-up">
      <div className="bg-white rounded-xl shadow-lg border border-stone-200 p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-stone-400 hover:text-stone-600"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#D97757] flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-stone-900 text-sm">Install BeautyPro</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {showIOSPrompt 
                ? "Tap the share button, then 'Add to Home Screen'"
                : "Add to your home screen for quick access"
              }
            </p>
          </div>
        </div>

        {installPrompt && (
          <Button 
            onClick={handleInstall}
            className="w-full mt-3 h-9 rounded-full bg-[#D97757] text-white hover:bg-[#C06040] text-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Install App
          </Button>
        )}

        {showIOSPrompt && (
          <div className="mt-3 p-2 bg-stone-50 rounded-lg">
            <p className="text-xs text-stone-600 text-center">
              Tap <span className="inline-block px-1">
                <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </span> then "Add to Home Screen"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
